import { Card, SectionTitle } from "@/components/ui-kit";
import { User, Monitor, Cpu, Layers, Bot, CircuitBoard, Camera, ArrowDown } from "lucide-react";

const Node = ({ icon: Icon, title, sub, tone = "slate" }) => {
  const tones = {
    blue: "border-blue-700 bg-blue-950/30 text-blue-300",
    emerald: "border-emerald-700 bg-emerald-950/30 text-emerald-300",
    slate: "border-slate-700 bg-[#0A1128] text-slate-200",
  };
  return (
    <div className={`flex items-center gap-3 px-5 py-3.5 rounded-md border ${tones[tone]} w-full max-w-md`}>
      <Icon className="h-5 w-5 shrink-0" />
      <div>
        <div className="font-display font-semibold text-sm">{title}</div>
        {sub && <div className="text-xs opacity-70">{sub}</div>}
      </div>
    </div>
  );
};

const Arrow = () => (
  <div className="flex justify-center py-1"><ArrowDown className="h-5 w-5 text-slate-600" /></div>
);

export default function Architecture() {
  return (
    <div className="space-y-6 ngx-fade-in">
      <SectionTitle sub="Vendor-agnostic architecture for partnership demonstrations.">Platform Architecture</SectionTitle>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Flow diagram */}
        <Card className="p-8 relative overflow-hidden">
          <div className="absolute inset-0 ngx-grid-overlay opacity-15" />
          <div className="relative z-10 flex flex-col items-center">
            <Node icon={Layers} title="Industrial Hardware" sub="Robots · Machines · PLCs" />
            <Arrow />
            <Node icon={Monitor} title="NGX Human Control Station" sub="Operator supervision & decisions" tone="blue" />
            <Arrow />
            <Node icon={Cpu} title="NGX Core" sub="Event Engine · Decision Engine" tone="emerald" />
            <Arrow />
            <Node icon={Layers} title="Integration / Connector Layer" sub="Modular · plug & play" />
            <div className="flex justify-center py-2"><ArrowDown className="h-5 w-5 text-slate-600" /></div>
            <div className="grid grid-cols-3 gap-3 w-full max-w-md">
              <MiniNode icon={Bot} label="Robot" />
              <MiniNode icon={CircuitBoard} label="PLC" />
              <MiniNode icon={Camera} label="AI / Vision" />
            </div>
          </div>
        </Card>

        {/* Positioning */}
        <div className="space-y-6">
          <Card className="p-6">
            <h3 className="font-display text-lg font-bold text-slate-100 mb-2">NGX Human-in-the-Loop Platform</h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              A vendor-agnostic human supervision and decision layer for intelligent industrial systems. The initial MVP
              demonstrates human intervention in simulated industrial automation. The long-term architecture is intended
              to support robots, machines, PLCs, computer vision and intelligent industrial systems through modular connectors.
            </p>
          </Card>

          <Card className="p-6">
            <h3 className="font-display font-semibold text-slate-200 mb-4">Why It Matters</h3>
            <ul className="space-y-3 text-sm text-slate-300">
              <Li>Not every industrial decision should — or can — be fully autonomous.</Li>
              <Li>Human judgment where autonomy needs it: the right person, in the right moment, in the right context.</Li>
              <Li>Full traceability — every decision is recorded and auditable for quality and compliance.</Li>
              <Li>No dependence on a single robot manufacturer.</Li>
            </ul>
          </Card>

          <Card className="p-6 border-blue-900/50 bg-blue-950/10">
            <div className="text-xs font-semibold uppercase tracking-widest text-blue-400 mb-2">From Simulation to Industrial Validation</div>
            <p className="text-sm text-slate-300">
              What is needed from a partner: a real robot, machine, automation system, API or industrial use case that can
              replace the simulator during the next validation phase.
            </p>
            <div className="mt-4 flex items-center gap-2 text-xs font-mono">
              {["01 Concept", "02 MVP", "03 Pilot", "04 Platform"].map((s, i) => (
                <span key={s} className={`px-2.5 py-1 rounded-sm border ${i <= 1 ? "border-blue-700 bg-blue-950/40 text-blue-300" : "border-slate-700 text-slate-500"}`}>
                  {s}
                </span>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

const MiniNode = ({ icon: Icon, label }) => (
  <div className="flex flex-col items-center gap-1.5 px-3 py-3 rounded-md border border-slate-700 bg-[#050B14]">
    <Icon className="h-4 w-4 text-slate-400" />
    <span className="text-[11px] font-mono text-slate-400">{label}</span>
  </div>
);

const Li = ({ children }) => (
  <li className="flex items-start gap-2">
    <span className="h-1.5 w-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0" /> {children}
  </li>
);
